import { getEnvVar } from './utils/env';

// Env name defines a set of env variables used for app launch.
// See: https://vercel.com/takeda/buyeasy/settings/environment-variables
export type EnvName = 'local' | 'development' | 'staging' | 'production';
const envName = getEnvVar({
  NEXT_PUBLIC_BUYEASY_ENV: process.env.NEXT_PUBLIC_BUYEASY_ENV,
}) as EnvName;

// Build mode is how the app was built and started:
// 1. dev: started as 'next dev'
// 2. prod: started as 'next build' + 'next start'
export type BuildMode = 'dev' | 'prod';
const buildMode: BuildMode = process.env.NODE_ENV === 'development' ? 'dev' : 'prod';

/**
 * See: https://nextjs.org/docs/basic-features/environment-variables
 */
export const env = {
  name: envName,
  isProduction: envName === 'production',
  isLocal: envName === 'local',
  marketplaceHost: getEnvVar({
    NEXT_PUBLIC_MARKETPLACE_HOST: process.env.NEXT_PUBLIC_MARKETPLACE_HOST,
  }),
  build: {
    mode: buildMode,
    time: process.env.BUILD_TIME || '',
    sha: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || '',
    branch: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF || 'local',
    version: process.env.APP_VERSION || 'unknown',
  },
  aad: {
    tenantId: getEnvVar({ AZURE_AD_TENANT_ID: process.env.AZURE_AD_TENANT_ID }),
    clientId: getEnvVar({ AZURE_AD_CLIENT_ID: process.env.AZURE_AD_CLIENT_ID }),
    clientSecret: getEnvVar({ AZURE_AD_CLIENT_SECRET: process.env.AZURE_AD_CLIENT_SECRET }),
  },
  algolia: {
    appId: getEnvVar({ NEXT_PUBLIC_ALGOLIA_APP_ID: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID }),
    // ALGOLIA_API_KEY must be without NEXT_PUBLIC prefix as we use it on backend only
    apiKey: getEnvVar({ ALGOLIA_API_KEY: process.env.ALGOLIA_API_KEY }),
    poIndexName: getEnvVar({
      NEXT_PUBLIC_ALGOLIA_STATUS_INDEX_PO: process.env.NEXT_PUBLIC_ALGOLIA_STATUS_INDEX_PO,
    }),
    prIndexName: getEnvVar({
      NEXT_PUBLIC_ALGOLIA_STATUS_INDEX_PR: process.env.NEXT_PUBLIC_ALGOLIA_STATUS_INDEX_PR,
    }),
    ivtIndexName: getEnvVar({
      NEXT_PUBLIC_ALGOLIA_STATUS_INDEX_IVT: process.env.NEXT_PUBLIC_ALGOLIA_STATUS_INDEX_IVT,
    }),
    crIndexName: getEnvVar({
      NEXT_PUBLIC_ALGOLIA_STATUS_INDEX_CR: process.env.NEXT_PUBLIC_ALGOLIA_STATUS_INDEX_CR,
    }),
    suppliersIndexName: getEnvVar({
      NEXT_PUBLIC_ALGOLIA_SUPPLIERS_INDEX: process.env.NEXT_PUBLIC_ALGOLIA_SUPPLIERS_INDEX,
    }),
    aribaUserIndexName: getEnvVar({
      NEXT_PUBLIC_ALGOLIA_ARIBAUSER_INDEX: process.env.NEXT_PUBLIC_ALGOLIA_ARIBAUSER_INDEX,
    }),
    subcategoryIndexName: getEnvVar({
      NEXT_PUBLIC_ALGOLIA_SUBCATEGORY_INDEX: process.env.NEXT_PUBLIC_ALGOLIA_SUBCATEGORY_INDEX,
    }),
  },
  appInsights: {
    connectionString: getEnvVar({
      NEXT_PUBLIC_APP_INSIGHTS_CONNECTION_STRING:
        process.env.NEXT_PUBLIC_APP_INSIGHTS_CONNECTION_STRING,
    }),
  },
  procurementApi: {
    url: getEnvVar({ PROCUREMENT_API_URL: process.env.PROCUREMENT_API_URL }),
  },
  suppliersApi: {
    awsBaseUrl: getEnvVar({ SUPPLIER_API_AWS_BASE_URL: process.env.SUPPLIER_API_AWS_BASE_URL }),
    awsKey: getEnvVar({ SUPPLIER_API_AWS_KEY: process.env.SUPPLIER_API_AWS_KEY }),
  },
  configcat: {
    configcatKey: getEnvVar({
      NEXT_PUBLIC_CONFIGCAT_KEY_V2: process.env.NEXT_PUBLIC_CONFIGCAT_KEY_V2,
    }),
  },
  sanity: {
    projectId: getEnvVar({ SANITY_PROJECT_ID: process.env.SANITY_PROJECT_ID }),
    dataset: getEnvVar({ SANITY_DATASET: process.env.SANITY_DATASET }),
    token: getEnvVar({ SANITY_TOKEN: process.env.SANITY_TOKEN }),
    revalidateSecret: getEnvVar({ SANITY_REVALIDATE_SECRET: process.env.SANITY_REVALIDATE_SECRET }),
    // When set to any non-empty value, Sanity-managed data is read from committed JSON files
    // instead of the Sanity API. Read directly (not via getEnvVar) because the flag is optional.
    useJsonFiles: Boolean(process.env.SANITY_USE_JSON_FILES),
  },
  ownApi: {
    secret: getEnvVar({ OWN_API_SECRET: process.env.OWN_API_SECRET }),
  },
  aribaUrl: getEnvVar({ ARIBA_URL: process.env.ARIBA_URL }),
  invoiceTrackUrl: getEnvVar({ INVOICETRACK_URL: process.env.INVOICETRACK_URL }),
  testUserPassword: getEnvVar({ TEST_USER_PASSWORD: process.env.TEST_USER_PASSWORD }),
};
